import { Component, ElementRef, Injector, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { InvestmentComparablesRow } from '../../models/investmentcomparablesrow';
import { WidgetBaseComponent } from '../../widgetBase';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { AppServiceHelperService } from '../../../../../services/app-service-helper.service';

/**
 * InvestmentComparables Widget
 */
@Component({
  selector: 'investment-comparables-widget',
  templateUrl: './investment-comparables-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './investment-comparables-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class InvestmentComparablesWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Resize check reference
   */
  @ViewChild('resizeGridCheck') set resizeGridCheck(elemRef: ElementRef) {
    if (elemRef) { this.addTableResize(elemRef.nativeElement); }
  }

  /**
   * Add the table resize handler
   * @param tr
   * @returns
   */
  addTableResize(tr: any): void {
    if (tr == null) return;

    let headerHeight = 0;
    let row = tr;
    while (row?.classList.contains('resizing-col')) {
      headerHeight += row.clientHeight;
      row = row.nextElementSibling;
    }

    let index = this.showRefColumn() ? 0 : 1;
    for (const td of tr.cells) {
      const div = document.createElement('div');
      div.style.height = headerHeight + 'px';
      div.className = 'resizing-col-selector';
      td.appendChild(div);
      this.setResizeListeners(div, index);
      ++index;
    }
  }

  /**
   * Sets the resize listeners
   * @param div
   * @param index
   */
  setResizeListeners(div, index): void {
    let pageX, curColWidth, nextColWidth, allowedMoveRight, allowedMoveLeft;
    const lowerBound = 25;

    const onMouseUp = (e): void => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      div.classList.remove("currently-resizing");
    }

    const onMouseMove = (e): void => {
      const moveX = e.pageX - pageX;

      if (moveX < allowedMoveLeft) {
        this.cols[index] = lowerBound;
        this.cols[index + 1] = nextColWidth + curColWidth - lowerBound;
        return;
      }
      else if (moveX > allowedMoveRight) {
        this.cols[index + 1] = lowerBound;
        this.cols[index] = curColWidth + nextColWidth - lowerBound;
        return;
      }

      this.cols[index] = curColWidth + moveX;
      this.cols[index + 1] = nextColWidth - moveX;
    }

    div.addEventListener('mousedown', (e) => {
      pageX = e.pageX;
      curColWidth = this.cols[index];
      nextColWidth = this.cols[index + 1];
      allowedMoveRight = (nextColWidth - lowerBound);
      allowedMoveLeft = (-curColWidth + lowerBound);

      div.classList.add("currently-resizing");

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });

  }

  /**
   * Constructor
   * @param injector
   * @param httpClient
   * @param appServiceHelper
   */
  constructor(injector: Injector, private httpClient: HttpClient, private appServiceHelper: AppServiceHelperService) {
    super(injector, 'investmentComparables', false);
  }

  /**
   * Columns width definition
   */
  cols: Array<number>;

  /**
   * Columns colspan definition for edit mode
   */
  editModeCols: Array<number> = [
    4,// ref
    6,// transactionDate
    6,// size
    6,// vacancy
    12,// tenant
    8,// passingRent
    8,// price
    6,// pricePerArea
    8,// yieldMultiplier
    14// comment
  ];

  /**
   * Gets the tenant column width
   * @returns
   */
  public getTenantColSize(): number {
    let size = this.cols[4];
    if (this.currentLayout === 'Short') size += this.cols[9];//Give Comment col Size to Tenant in 'Short' Layout
    if (!this.editMode && !this.showRefColumn()) size += this.cols[0];//Give Ref col Size to Tenant when it isn't displayed 

    return size;
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready

    this.initWidgetConfig();
    this.widgetResized();

    this.cols = this.widgetConfig.widgetSettings?.layoutConfig?.cols || [//1098
      34,// ref
      62,// transactionDate
      90,// size
      90,// vacancy
      343,// tenant
      118,// passingRent
      118,// price
      62,// pricePerArea
      62,// yieldMultiplier
      118// comment
    ];

    if (!this.widgetConfig.widgetSettings?.layoutConfig) {
      this.widgetConfig.widgetSettings.layoutConfig = { cols: this.cols };
    }
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("investmentComparables", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.mockupData$ = this.widgetService.loadMockup('investmentcomparables');

    this.loadReflist('ref_l3_yield_multiplier');

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('investmentcomparables', this.getReport());
  }

  /**
   * Sufix for price per area values
   */
  get pricePerAreaSuffix() {
    return `/${this.unitOfArea}`;
  }

  /**
   * Add row
   */
  addRow() {
    if (!this.widgetData.rows) {
      this.widgetData.rows = [];
    }
    this.widgetData.rows.push(new InvestmentComparablesRow());
    this.setDirtyFlag(true, true);
  }

  /**
   * Remove row
   * @param row {InvestmentComparablesRow} Row to be removed
   */
  removeRow(row: InvestmentComparablesRow) {
    const index = this.widgetData.rows.indexOf(row, 0);
    if (index > -1) {
      this.widgetData.rows.splice(index, 1);
    }
    this.setDirtyFlag(true, true);
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Loads PixComps data
   */
  getPixCompsData() {
    this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportwidgets/investmentcomparables/getPixCompsData?oid=' + this.getReport()))
      .pipe(take(1)).subscribe((data) => {
        if (data == null || data.rows.length === 0) {
          this.eventBus.broadcastEvent("openConfirmDialog", {
            success: () => {
              //Nothing
            }
          }, 'infobarPixCompsDataNotAvailableConfirmDialog');
        } else {
          //14959: overwrite only if data is available
          this.widgetData = null;
          setTimeout(() => {
            for (const row of data.rows) {
              row.yieldMultiplier = row.yieldMultiplier * 100;
            }
            this.widgetData = data;

            this.setDirtyFlag(true, true);
          });
        }
      });
  }

  /**
   * Should ref column be shown in widget?
   */
  public showRefColumn(): boolean {
    for (const row of this.widgetData.rows) {
      if (row.ref) {
        return true;
      }
    }
    return false;
  }

  /**
   * Is the value a number?
   * @param value
   * @returns
   */
  public isNumber(value: string): boolean {
    return !isNaN(value as any);
  }
}
